import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import * as InvitesGet from '../../../server/routes/orgs/invites/get';
import * as InvitesDelete from '../../../server/routes/users/invites/delete';
import * as InviteSend from '../../../server/routes/users/invites/post';
import * as UserNamePatch from '../../../server/routes/users/name/patch';
import {
  generatePath,
  nameMaxLength,
} from '../../../server/services/utils.service';
import type { Org, OrgUser } from '../../../server/types/org-types';
import type { DeepReadonly } from '../../../server/util-type';
import { AuthUserService } from '../auth-user.service';
import { TitleService } from '../title.service';
import {
  UserAdminAddDialogComponent,
  type UserAdminAddDialogComponentData,
} from '../user-admin-add-dialog/user-admin-add-dialog.component';
import {
  UserAdminRemoveDialogComponent,
  type UserAdminRemoveDialogComponentData,
} from '../user-admin-remove-dialog/user-admin-remove-dialog.component';
import {
  UserArchiveDialogComponent,
  type UserArchiveDialogComponentData,
} from '../user-archive-dialog/user-archive-dialog.component';

@Component({
  selector: 'app-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss'],
  standalone: false,
})
export class UserEditComponent implements OnInit, OnDestroy {
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);
  private authUserService = inject(AuthUserService);
  private dialog = inject(MatDialog);
  private titleService = inject(TitleService);
  private route = inject(ActivatedRoute);

  org: DeepReadonly<Org>;
  user: DeepReadonly<OrgUser>;

  name: string;
  canEditName = false;
  canInvite = false;
  canArchive = false;
  canMakeAdmin = false;
  canRemoveAdmin = false;
  authUserIsAdmin = false;
  nameControl: FormControl<string>;
  nameChanged = false;
  nameUpdateState: 'none' | 'waiting' | 'complete' = 'none';

  pageState: 'waiting' | 'ready' | 'error' = 'waiting';

  invite = false;

  subscriptions = new Subscription();

  ngOnInit(): void {
    this.titleService.set('Edit user');

    const sub = this.authUserService.userOrgObj.subscribe((userOrgObj) => {
      if (!userOrgObj) return; // This should not happen except at logout

      this.org = userOrgObj.org;

      const userId: string = this.route.snapshot.params.userId;
      const authUserId = userOrgObj.user._id;
      this.authUserIsAdmin = userOrgObj.user.admin;

      const user = this.org.users.find((u) => u._id === userId);
      if (!user) {
        this.pageState = 'error';
        return;
      }
      this.user = user;

      this.canInvite = this.authUserIsAdmin && !this.user.accountId;

      this.canArchive =
        this.authUserIsAdmin && !this.user.archivedAt && userId !== authUserId;

      // Name control
      this.name = this.user.name;
      this.canEditName = userOrgObj.user.admin;
      if (this.nameControl) {
        this.nameControl.setValue(this.name);
      } else {
        this.nameControl = new FormControl(this.name, {
          nonNullable: true,
          validators: [Validators.maxLength(nameMaxLength)],
        });
        const sub1 = this.nameControl.valueChanges.subscribe((value) => {
          this.nameChanged = value !== this.name;
          if (this.nameChanged && this.nameUpdateState === 'complete') {
            this.nameUpdateState = 'none';
          }
        });
        this.subscriptions.add(sub1);
      }

      // Get invite
      const path = generatePath<InvitesGet.ReqParams>(
        '/api/orgs/:orgId/invites',
        { orgId: this.org._id }
      );

      let params: HttpParams | undefined = new HttpParams();
      params = params.append('userId', userId);

      const subInviteGet = this.http
        .get<InvitesGet.ResBody>(path, { params })
        .subscribe({
          next: (invites) => {
            if (invites.length) this.invite = true;
            this.determineCanInvite();
          },
          error: (err) => {
            //
          },
        });
      this.subscriptions.add(subInviteGet);

      // Get admin
      this.canMakeAdmin = this.authUserIsAdmin && !this.user.admin;
      this.canRemoveAdmin =
        this.authUserIsAdmin && this.user.admin && userId !== authUserId; // Cannot remove self as admin

      this.pageState = 'ready';
    });
    this.subscriptions.add(sub);
  }

  saveName() {
    const uncleanName = this.nameControl.value;
    const name = uncleanName.trim();
    const path = generatePath<UserNamePatch.ReqParams>(
      '/api/orgs/:orgId/users/:userId/name',
      { orgId: this.org._id, userId: this.user._id }
    );
    this.nameChanged = false;
    const body: UserNamePatch.ReqBody = { name };
    this.nameUpdateState = 'waiting';
    const sub = this.http.patch<UserNamePatch.ResBody>(path, body).subscribe({
      next: ({ org }) => {
        this.nameUpdateState = 'complete';
        this.authUserService.updateOrg({ org });
      },
      error: (err) => {
        this.nameUpdateState = 'none';
        this.snackBar.open('ERROR: could not update user name.', 'Dismiss', {
          duration: 5000,
        });
      },
    });
    this.subscriptions.add(sub);
  }

  archiveUser() {
    const dialogConfig = new MatDialogConfig();
    const { org, user } = this;
    const data: UserArchiveDialogComponentData = { org, user };
    dialogConfig.data = data;
    this.dialog.open(UserArchiveDialogComponent, dialogConfig);
  }

  private determineCanInvite() {
    this.canInvite =
      this.authUserIsAdmin && !this.user.accountId && !this.invite;
  }

  sendInvite() {
    const body: InviteSend.ReqBody = {};
    const path = generatePath<InviteSend.ReqParams>(
      '/api/orgs/:orgId/users/:userId/invites',
      { orgId: this.org._id, userId: this.user._id }
    );
    const sub = this.http.post<InviteSend.ResBody>(path, body).subscribe({
      next: () => {
        this.invite = true;
        this.determineCanInvite();
      },
      error: (err) => {
        //
      },
    });
    this.subscriptions.add(sub);
  }

  cancelInvite() {
    const path = generatePath<InvitesDelete.ReqParams>(
      '/api/orgs/:orgId/users/:userId/invites',
      { orgId: this.org._id, userId: this.user._id }
    );
    const sub = this.http.delete<InvitesDelete.ResBody>(path).subscribe({
      next: () => {
        this.invite = false;
        this.determineCanInvite();
      },
      error: (err) => {
        //
      },
    });
    this.subscriptions.add(sub);
  }

  makeAdmin() {
    const dialogConfig = new MatDialogConfig();
    const { org, user } = this;
    const data: UserAdminAddDialogComponentData = { org, user };
    dialogConfig.data = data;
    this.dialog.open(UserAdminAddDialogComponent, dialogConfig);
  }

  removeAdmin() {
    const dialogConfig = new MatDialogConfig();
    const { org, user } = this;
    const data: UserAdminRemoveDialogComponentData = { org, user };
    dialogConfig.data = data;
    this.dialog.open(UserAdminRemoveDialogComponent, dialogConfig);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
